import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  databaseMigrationsApi,
  type MigrationRun,
  type MigrationErrorKind,
  type RunDetail,
  type StatementKind,
} from '../../../api/databaseMigrations';

/** How many runs to show before the operator asks for more. The history grows
 *  without bound — 89 rows in this deployment — and an un-capped table buried
 *  the two runs anyone actually cared about. */
const PAGE = 12;

const ERROR_KIND_LABEL: Record<MigrationErrorKind, string> = {
  already_applied: 'Already applied',
  data_conflict: 'Blocked by existing data',
  missing_object: 'Object missing',
  syntax: 'SQL error',
  permission: 'Permission denied',
  unknown: 'Failed',
};

/** `already_applied` is not a failure — the change is in place. Colour it so a
 *  scan of the list separates "nothing to do" from "someone must act". */
const ERROR_KIND_BADGE: Record<MigrationErrorKind, string> = {
  already_applied: 'bg-emerald-100 text-emerald-700',
  data_conflict: 'bg-red-100 text-red-700',
  missing_object: 'bg-amber-100 text-amber-800',
  syntax: 'bg-red-100 text-red-700',
  permission: 'bg-red-100 text-red-700',
  unknown: 'bg-red-100 text-red-700',
};

/** The first failure in a run that has something to say. */
function firstFailure(targets: MigrationRun[]): MigrationRun | undefined {
  return targets.find((t) => t.status === 'failed' && (t.error_reason || t.error_message));
}

interface Props {
  scriptId?: number;
  refreshKey?: number;
}

const STATUS_BADGE: Record<MigrationRun['status'], string> = {
  pending: 'bg-slate-100 text-slate-600',
  running: 'bg-blue-100 text-blue-700',
  success: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  rolled_back: 'bg-amber-100 text-amber-700',
};

const KIND_BADGE: Record<StatementKind, string> = {
  DDL: 'bg-blue-100 text-blue-700 border-blue-200',
  DML: 'bg-slate-100 text-slate-700 border-slate-200',
  DESTRUCTIVE: 'bg-red-100 text-red-700 border-red-200',
  READ: 'bg-gray-100 text-gray-600 border-gray-200',
};

interface GroupedRun {
  runId: string;
  earliest: string;
  targets: MigrationRun[];
  rollupStatus: MigrationRun['status'];
  totalDuration: number | null;
  executedBy: string;
}

function rollupStatus(targets: MigrationRun[]): MigrationRun['status'] {
  if (targets.some((t) => t.status === 'failed')) return 'failed';
  if (targets.some((t) => t.status === 'rolled_back')) return 'rolled_back';
  if (targets.some((t) => t.status === 'running')) return 'running';
  if (targets.every((t) => t.status === 'success')) return 'success';
  return 'pending';
}

const RunHistoryTable: React.FC<Props> = ({ scriptId, refreshKey }) => {
  const [runs, setRuns] = useState<MigrationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, RunDetail>>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [showRawSql, setShowRawSql] = useState<Record<string, boolean>>({});
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [visible, setVisible] = useState(PAGE);

  useEffect(() => {
    setLoading(true);
    setVisible(PAGE);
    databaseMigrationsApi.listRuns({ scriptId, limit: 200 })
      .then(setRuns)
      .finally(() => setLoading(false));
  }, [scriptId, refreshKey]);

  const grouped: GroupedRun[] = useMemo(() => {
    const map = new Map<string, MigrationRun[]>();
    for (const r of runs) {
      const list = map.get(r.run_id) ?? [];
      list.push(r);
      map.set(r.run_id, list);
    }
    return Array.from(map.entries())
      .map(([runId, targets]) => {
        const sorted = targets.slice().sort((a, b) =>
          new Date(a.executed_at).getTime() - new Date(b.executed_at).getTime()
        );
        const totalDuration = sorted.every((t) => t.duration_ms == null)
          ? null
          : sorted.reduce((acc, t) => acc + (t.duration_ms ?? 0), 0);
        return {
          runId,
          earliest: sorted[0].executed_at,
          targets: sorted,
          rollupStatus: rollupStatus(sorted),
          totalDuration,
          executedBy: sorted[0].executed_by,
        };
      })
      .sort((a, b) => new Date(b.earliest).getTime() - new Date(a.earliest).getTime());
  }, [runs]);

  const handleToggle = async (runId: string) => {
    if (expanded === runId) {
      setExpanded(null);
      return;
    }
    setExpanded(runId);
    setDetailError(null);
    if (!details[runId]) {
      setDetailLoading(runId);
      try {
        const detail = await databaseMigrationsApi.getRun(runId);
        setDetails((prev) => ({ ...prev, [runId]: detail }));
      } catch (e) {
        setDetailError(e instanceof Error ? e.message : 'Failed to load run details');
      } finally {
        setDetailLoading(null);
      }
    }
  };

  // "Needs attention" excludes already-applied errors: those are runs where the
  // change was in place, which is the opposite of something to look at.
  const needsAttention = (g: GroupedRun) =>
    g.targets.some((t) => t.status === 'failed' && t.error_kind !== 'already_applied');

  const filtered = onlyProblems ? grouped.filter(needsAttention) : grouped;
  const shown = filtered.slice(0, visible);
  const problemCount = grouped.filter(needsAttention).length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
        <span className="text-sm font-medium text-slate-700">Recent runs</span>
        <span className="text-xs text-slate-400 tabular-nums">
          {filtered.length} of {grouped.length}
        </span>
        {problemCount > 0 && (
          <button
            type="button"
            onClick={() => { setOnlyProblems((v) => !v); setVisible(PAGE); }}
            aria-pressed={onlyProblems}
            className={`ml-auto rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
              onlyProblems
                ? 'border-red-300 bg-red-50 text-red-700'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            Needs attention ({problemCount})
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        {loading && <div className="p-4 text-sm text-slate-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-4 text-sm text-slate-500">
            {onlyProblems ? 'Nothing needs attention.' : 'No runs yet.'}
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-8 px-2 py-2"></th>
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Targets</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Duration</th>
                <th className="px-4 py-2">By</th>
                <th className="px-4 py-2">Run ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((g) => {
                const isOpen = expanded === g.runId;
                const detail = details[g.runId];
                const successCount = g.targets.filter((t) => t.status === 'success').length;
                const failCount = g.targets.filter((t) => t.status === 'failed').length;
                return (
                  <React.Fragment key={g.runId}>
                    <tr
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => handleToggle(g.runId)}
                    >
                      <td className="px-2 py-2 text-slate-400">
                        {isOpen
                          ? <ChevronDownIcon className="w-4 h-4" />
                          : <ChevronRightIcon className="w-4 h-4" />}
                      </td>
                      <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                        {new Date(g.earliest).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 font-mono text-slate-800">
                        {g.targets.length === 1
                          ? g.targets[0].target_db
                          : `${g.targets.length} targets · ${successCount} ok${failCount ? ` · ${failCount} failed` : ''}`}
                      </td>
                      <td className="px-4 py-2">
                        {(() => {
                          // A run that failed ONLY because the change was
                          // already present is not a failure worth alarming
                          // over — label it by cause, not by status.
                          const f = firstFailure(g.targets);
                          const kind = f?.error_kind;
                          if (g.rollupStatus === 'failed' && kind) {
                            return (
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${ERROR_KIND_BADGE[kind]}`}>
                                {ERROR_KIND_LABEL[kind]}
                              </span>
                            );
                          }
                          return (
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${STATUS_BADGE[g.rollupStatus]}`}>
                              {g.rollupStatus}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-2 text-slate-600">
                        {g.totalDuration != null ? `${g.totalDuration}ms` : '—'}
                      </td>
                      <td className="px-4 py-2 text-slate-600">{g.executedBy}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-400">{g.runId.slice(0, 8)}</td>
                    </tr>
                    {/* The reason, inline. Previously this lived only inside
                        the expanded detail, behind a second request — so the
                        list said "failed" and nothing else. */}
                    {!isOpen && (() => {
                      const f = firstFailure(g.targets);
                      if (!f) return null;
                      const isBenign = f.error_kind === 'already_applied';
                      return (
                        <tr
                          className={isBenign ? 'bg-emerald-50/40' : 'bg-red-50/40'}
                          onClick={() => handleToggle(g.runId)}
                        >
                          <td />
                          <td colSpan={6} className="px-4 pb-2 pt-0">
                            <div className={`text-xs leading-relaxed ${isBenign ? 'text-emerald-800' : 'text-red-800'}`}>
                              <span className="font-medium">{f.error_reason ?? 'Failed'}</span>
                              {f.error_detail && f.error_detail !== f.error_reason && (
                                <span className="ml-2 font-mono text-[11px] opacity-80">{f.error_detail}</span>
                              )}
                              {isBenign && (
                                <span className="ml-2 opacity-90">
                                  — this migration is already in place; record it as applied to clear it.
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                    {isOpen && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={7} className="px-6 py-4">
                          {detailLoading === g.runId && (
                            <div className="text-sm text-slate-500">Loading run details…</div>
                          )}
                          {detailError && expanded === g.runId && (
                            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                              {detailError}
                            </div>
                          )}
                          {detail && (
                            <RunDetailPanel
                              detail={detail}
                              showRaw={!!showRawSql[g.runId]}
                              onToggleRaw={() =>
                                setShowRawSql((p) => ({ ...p, [g.runId]: !p[g.runId] }))
                              }
                            />
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && filtered.length > shown.length && (
          <button
            type="button"
            onClick={() => setVisible((v) => v + PAGE)}
            className="w-full border-t border-slate-100 px-4 py-2.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Show {Math.min(PAGE, filtered.length - shown.length)} more
            <span className="ml-1.5 text-slate-400 tabular-nums">
              ({filtered.length - shown.length} remaining)
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

const RunDetailPanel: React.FC<{
  detail: RunDetail;
  showRaw: boolean;
  onToggleRaw: () => void;
}> = ({ detail, showRaw, onToggleRaw }) => {
  const failedTargets = detail.targets.filter((t) => t.status === 'failed');
  const succeededTargets = detail.targets.filter((t) => t.status === 'success');

  return (
    <div className="space-y-4">
      {/* Per-target status */}
      {detail.targets.length > 1 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Per-target status
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {detail.targets.map((t) => (
              <div
                key={t.id}
                className={`rounded-lg border px-3 py-2 text-sm flex items-center justify-between ${
                  t.status === 'failed'
                    ? 'border-red-200 bg-red-50'
                    : t.status === 'success'
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {t.companyName || t.targetDb}
                    {t.companyCode && (
                      <span className="ml-2 text-xs font-mono text-slate-500">{t.companyCode}</span>
                    )}
                  </div>
                  <div className="text-xs font-mono text-slate-500 truncate">{t.targetDb}</div>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${STATUS_BADGE[t.status]}`}>
                  {t.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Failed target errors */}
      {failedTargets.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-red-700 mb-2">
            Errors ({failedTargets.length})
          </div>
          <div className="space-y-2">
            {failedTargets.map((t) => (
              <div key={t.id} className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="text-sm font-medium text-red-900 mb-1">
                  {t.companyName || t.targetDb}
                  <span className="ml-2 text-xs font-mono text-red-700">{t.targetDb}</span>
                </div>
                <pre className="text-xs text-red-800 whitespace-pre-wrap break-words font-mono leading-relaxed">
                  {t.errorMessage || '(no error message recorded)'}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* What changed */}
      {detail.statements.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
            <span>What changed</span>
            <span className="font-normal normal-case text-slate-400">
              ({detail.summary.counts.DDL} DDL · {detail.summary.counts.DML} DML
              {detail.summary.counts.DESTRUCTIVE > 0 && ` · ${detail.summary.counts.DESTRUCTIVE} destructive`}
              {detail.summary.counts.READ > 0 && ` · ${detail.summary.counts.READ} read`})
            </span>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
            {detail.statements.map((s, idx) => (
              <div key={idx} className="px-3 py-2 flex items-start gap-3 text-sm">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border shrink-0 mt-0.5 ${KIND_BADGE[s.kind]}`}>
                  {s.kind}
                </span>
                <span className="text-slate-800 leading-relaxed flex-1 min-w-0">{s.summary}</span>
              </div>
            ))}
          </div>
          {failedTargets.length === 0 && succeededTargets.length > 0 && (
            <div className="text-xs text-emerald-700 mt-1.5">
              Applied successfully to {succeededTargets.length} target{succeededTargets.length === 1 ? '' : 's'}.
            </div>
          )}
        </div>
      )}

      {/* Raw SQL toggle */}
      <div>
        <button
          type="button"
          onClick={onToggleRaw}
          className="text-xs font-medium text-slate-600 hover:text-slate-900 underline"
        >
          {showRaw ? 'Hide raw SQL' : 'Show raw SQL'}
        </button>
        {showRaw && (
          <pre className="mt-2 bg-slate-900 text-slate-100 rounded-lg p-3 text-xs font-mono leading-relaxed overflow-x-auto whitespace-pre">
            {detail.sqlSnapshot}
          </pre>
        )}
      </div>
    </div>
  );
};

export default RunHistoryTable;
